// The server's wall clock, as seen from this device.
//
// Anything that compares a server-stamped TIME with "now" — an NJ Transit bus's fix age, which
// drives where it is predicted and whether it is drawn stale — must use this, not Date.now():
// a phone's clock a minute off would otherwise freeze every bus or fade the whole fleet.
// (Feed health avoids the problem differently: the server sends ages, not times — see
// store/system.ts. A fix's time is per vehicle and travels inside cached payloads, so an age
// baked in at poll time would be stale by the time a client read it.)
//
// Every WebSocket frame carries the server's `Date.now()` from when it was stamped. Each one
// gives `stamp − receivedAt`, which is the true offset LESS the frame's latency (and less any
// time it sat between stamping and sending), so the estimate is the LARGEST of recent
// samples. Recent, not all-time: a device clock that is corrected mid-session must be able to
// take its old estimate with it.
//
// Framework-free.

const WINDOW = 12
const samples: number[] = []
let offsetMs = 0

/** Record a server timestamp (epoch ms) from a frame that has just arrived. */
export function noteServerTime(serverMs: number, receivedAtMs = Date.now()): void {
  if (!Number.isFinite(serverMs) || serverMs <= 0) return
  samples.push(serverMs - receivedAtMs)
  if (samples.length > WINDOW) samples.shift()
  offsetMs = Math.max(...samples)
}

/** Now by the server's clock, epoch seconds. Device time until a frame has arrived. */
export function serverNowSec(deviceNowMs = Date.now()): number {
  return (deviceNowMs + offsetMs) / 1000
}

/** Tests only. */
export function __resetServerClock(): void {
  samples.length = 0
  offsetMs = 0
}
